import { PageSize } from '@/constant';
import {
	IDeviceMobileDetailResponse,
	IDeviceMobileItem,
	IGetDeviceMobileListParams,
} from '@/services/api/api.type';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
	IPayloadApproveMobileDeviceRequest,
	IPayloadGetDeviceMobileDetailRequest,
	IPayloadGetDeviceMobileDetailSuccess,
	IPayloadGetDeviceMobileListRequest,
	IPayloadGetDeviceMobileListSuccess,
	IPayloadReapproveMobileDeviceRequest,
	IPayloadRejectMobileDeviceRequest,
	IPayloadRevokeMobileDeviceRequest,
} from './device-mobile.type';

const InitParams: IGetDeviceMobileListParams = {
	page: 1,
	pageSize: PageSize.XS,
};

const initialState = {
	fetching: false,
	list: [] as IDeviceMobileItem[],
	total: 0,
	params: InitParams,
	// Detail
	detail: null as IDeviceMobileDetailResponse | null,
	fetchingDetail: false,
	// Actions
	submitting: false,
};

const deviceMobileSlice = createSlice({
	initialState,
	name: 'deviceMobile',
	reducers: {
		/**---- GET DEVICE_MOBILE LIST START ---- */
		getDeviceMobileListRequest: (
			state,
			_action: PayloadAction<IPayloadGetDeviceMobileListRequest>
		) => {
			state.fetching = true;
			state.params = _action?.payload;
		},
		getDeviceMobileListSuccess: (
			state,
			action: PayloadAction<IPayloadGetDeviceMobileListSuccess>
		) => {
			const { list, totalCount } = action.payload;
			state.fetching = false;
			state.list = list;
			state.total = totalCount;
		},
		getDeviceMobileListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET DEVICE_MOBILE LIST END ---- */

		/**---- GET DEVICE_MOBILE DETAIL START ---- */
		getDeviceMobileDetailRequest: (
			state,
			_action: PayloadAction<IPayloadGetDeviceMobileDetailRequest>
		) => {
			state.fetchingDetail = true;
			state.detail = null;
		},
		getDeviceMobileDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetDeviceMobileDetailSuccess>
		) => {
			state.fetchingDetail = false;
			state.detail = action.payload.detail;
		},
		getDeviceMobileDetailFailure: (state) => {
			state.fetchingDetail = false;
		},
		clearDeviceMobileDetail: (state) => {
			state.detail = null;
		},
		/**---- GET DEVICE_MOBILE DETAIL END ---- */

		/**---- APPROVE MOBILE DEVICE START ---- */
		approveMobileDeviceRequest: (
			state,
			_action: PayloadAction<IPayloadApproveMobileDeviceRequest>
		) => {
			state.submitting = true;
		},
		approveMobileDeviceSuccess: (state) => {
			state.submitting = false;
		},
		approveMobileDeviceFailure: (state) => {
			state.submitting = false;
		},
		/**---- APPROVE MOBILE DEVICE END ---- */

		/**---- REJECT MOBILE DEVICE START ---- */
		rejectMobileDeviceRequest: (
			state,
			_action: PayloadAction<IPayloadRejectMobileDeviceRequest>
		) => {
			state.submitting = true;
		},
		rejectMobileDeviceSuccess: (state) => {
			state.submitting = false;
		},
		rejectMobileDeviceFailure: (state) => {
			state.submitting = false;
		},
		/**---- REJECT MOBILE DEVICE END ---- */

		/**---- REVOKE MOBILE DEVICE START ---- */
		revokeMobileDeviceRequest: (
			state,
			_action: PayloadAction<IPayloadRevokeMobileDeviceRequest>
		) => {
			state.submitting = true;
		},
		revokeMobileDeviceSuccess: (state) => {
			state.submitting = false;
		},
		revokeMobileDeviceFailure: (state) => {
			state.submitting = false;
		},
		/**---- REVOKE MOBILE DEVICE END ---- */

		/**---- REAPPROVE MOBILE DEVICE START ---- */
		reapproveMobileDeviceRequest: (
			state,
			_action: PayloadAction<IPayloadReapproveMobileDeviceRequest>
		) => {
			state.submitting = true;
		},
		reapproveMobileDeviceSuccess: (state) => {
			state.submitting = false;
		},
		reapproveMobileDeviceFailure: (state) => {
			state.submitting = false;
		},
		/**---- REAPPROVE MOBILE DEVICE END ---- */
	},
});

export const DeviceMobileSelectors = {
	selectFetching: (state: RootState) => state.deviceMobile.fetching,
	selectList: (state: RootState) => state.deviceMobile.list,
	selectTotal: (state: RootState) => state.deviceMobile.total,
	selectParams: (state: RootState) => state.deviceMobile.params,
	selectDetail: (state: RootState) => state.deviceMobile.detail,
	selectFetchingDetail: (state: RootState) => state.deviceMobile.fetchingDetail,
	selectSubmitting: (state: RootState) => state.deviceMobile.submitting,
};

export const DeviceMobileActions = deviceMobileSlice.actions;
export const DeviceMobileReducer = deviceMobileSlice.reducer;
